import { Row } from './Row'
import { Cell } from './Cell'
import { ReactNode } from 'react'
import { Currency } from '@sequencehq/api'
import {
  calculateDiscountedPrice,
  formatInteger,
  sanitizeTiersAndAddLowerBounds,
  toBillingFrequencyLabel,
  toDiscountAmount,
  toMoney,
  toPriceSummary,
  toBillingType,
  getHighestPrecision
} from '@sequencehq/utils'
import { match, Pattern } from 'ts-pattern'
import {
  Text,
  GreyGrey50,
  GreyGrey60,
  GreyGrey70,
  IconArrowCornerDownRight,
  IconArrowsRepeat,
  IconCircleX,
  IconPlus,
  IndigoIndigo60,
  Primary13Regular,
  Box,
  Flex
} from '@sequencehq/core-components'
import { toPercentage } from '@sequencehq/utils'
import {
  BillingFrequency,
  PhaseDiscount,
  Price
} from '../../atoms/reviewPricing.primitives.atoms'
import { useSetAtom } from 'jotai'
import { taskDrawerConfigAtom } from '../../../../../atoms'

const Header = () => {
  return (
    <Row
      style={{
        minHeight: '32px'
      }}
      alignCellContent="bottom"
      description={<Cell.Header value="Description" />}
      billingType={<Cell.Header value="Bill" />}
      billingFrequency={<Cell.Header value="Repeats" />}
      discount={<Cell.Header align="right" value="Discount" />}
      amount={<Cell.Header align="right" value="Amount" />}
    />
  )
}

const SchedulePriceTableComponent = ({ children }: { children: ReactNode }) => {
  return (
    <Flex flexDir="column" width="100%">
      {children}
    </Flex>
  )
}

const DiscountCell = ({
  amount,
  discountType,
  currency,
  'data-testid': testId,
  onClick
}: {
  amount: number
  discountType: PhaseDiscount['type']
  currency: Currency
  'data-testid'?: string
  onClick?: () => void
}) => {
  return (
    <Cell
      align="right"
      data-testid={testId}
      value={toDiscountAmount({
        discountCalculationType: discountType,
        discountAmount: amount,
        currency
      })}
      onClick={onClick}
    />
  )
}

const MinimumCell = ({
  amount,
  currency,
  billingFrequency,
  'data-testid': testId
}: {
  amount: number
  currency: Currency
  billingFrequency: BillingFrequency
  'data-testid'?: string
}) => {
  return (
    <Cell
      align="right"
      data-testid={testId}
      value={`${toMoney({
        value: amount.toString(),
        currency
      })} / ${toBillingFrequencyLabel(billingFrequency)}`}
    />
  )
}

const priceSummary = ({
  price,
  discountedPrice,
  minimumFractionDigits = 2,
  maximumFractionDigits = 10
}: {
  price: Price
  discountedPrice?: number | null
  minimumFractionDigits?: number
  maximumFractionDigits?: number
}) => {
  const formattedOriginalPrice = toPriceSummary(
    {
      ...price,
      integrationIds: [],
      customMetricParameters: []
    },
    {
      minimumFractionDigits,
      maximumFractionDigits
    }
  )

  const formattedDiscountedPrice =
    discountedPrice !== null && discountedPrice !== undefined
      ? toMoney({
          value: discountedPrice.toString(),
          currency: price.currency,
          maximumFractionDigits,
          minimumFractionDigits
        })
      : null

  return (
    <Flex gap="4px">
      {formattedDiscountedPrice && <Box>{formattedDiscountedPrice}</Box>}
      <Box
        textDecoration={formattedDiscountedPrice ? 'line-through' : undefined}
        color={formattedDiscountedPrice ? GreyGrey50 : undefined}
      >
        {formattedOriginalPrice}
      </Box>
    </Flex>
  )
}

const PriceCell = ({
  price,
  ownDiscount,
  'data-testid': testId
}: {
  price: Price
  ownDiscount?: PhaseDiscount
  'data-testid'?: string
}) => {
  /**
   * Only the price's own discount comes off the rate shown here. A phase-wide
   * discount covers the whole phase, so it gets its own row instead.
   */
  const discountedPrice = calculateDiscountedPrice({
    price,
    discount: ownDiscount && {
      ...ownDiscount,
      discountCalculationType: ownDiscount.type
    }
  })

  return match(price)
    .with(
      { structure: { pricingType: 'GRADUATED' } },
      { structure: { pricingType: 'VOLUME' } },
      {
        structure: {
          pricingType: 'SEAT_BASED',
          /**
           * SEAT_BASED in a bit wonky as it's actually two pricing models
           * smooshed into one, so we need to do some array length
           * duck typing to match it.
           */
          tiers: Pattern.when(arr => (arr?.length ?? 0) > 0)
        }
      },
      price => {
        const sanitizedTiers = sanitizeTiersAndAddLowerBounds(
          /**
           * This is needed because the seat based graduated type defines
           * tiers as undefined, and the matcher above doesn't seem to
           * - to TS at least - guarantee the array being present.
           */
          price.structure.tiers ?? []
        )
        const highestUnitPricePrecision = getHighestPrecision(
          sanitizedTiers.tiers.map(tier => tier.unitPrice)
        )
        const highestPercentageUnitPrecision = getHighestPrecision(
          sanitizedTiers.percentageTiers.map(tier => tier.unitPercentage)
        )

        return (
          <Cell
            align="right"
            data-testid={testId}
            value={priceSummary({
              price,
              discountedPrice,
              minimumFractionDigits: highestUnitPricePrecision,
              maximumFractionDigits: highestUnitPricePrecision
            })}
            otherRows={
              sanitizedTiers.usageTierType === 'FIXED'
                ? sanitizedTiers.tiers.map((tier, idx) => {
                    return (
                      <Flex color={GreyGrey50} key={idx}>
                        <Box marginRight="4px">
                          {formatInteger()(tier.firstUnit)}{' '}
                          {tier.lastUnit
                            ? `to ${formatInteger()(tier.lastUnit)}`
                            : `or more`}
                        </Box>
                        (
                        <Box color={GreyGrey70}>
                          {toMoney({
                            value: tier.unitPrice,
                            currency: price.currency,
                            maximumFractionDigits: highestUnitPricePrecision,
                            minimumFractionDigits: highestUnitPricePrecision
                          })}
                          /unit
                        </Box>
                        )
                      </Flex>
                    )
                  })
                : sanitizedTiers.percentageTiers.map((tier, idx) => {
                    return (
                      <Flex color={GreyGrey50} key={idx}>
                        <Box marginRight="4px" textAlign="right">
                          {toMoney({
                            value: tier.firstUnit,
                            currency: price.currency
                          })}{' '}
                          {tier.lastUnit
                            ? `to ${toMoney({ value: tier.lastUnit, currency: price.currency })}`
                            : `or more`}
                        </Box>
                        (
                        <Box color={GreyGrey70}>
                          {toPercentage(tier.unitPercentage, {
                            minimumFractionDigits:
                              highestPercentageUnitPrecision
                          })}
                        </Box>
                        )
                      </Flex>
                    )
                  })
            }
          />
        )
      }
    )
    .otherwise(price => (
      <Cell
        align="right"
        data-testid={testId}
        value={priceSummary({
          price,
          discountedPrice
        })}
      />
    ))
}

const DeleteCell = (props: {
  onDelete: () => void
  'data-testid'?: string
}) => {
  return (
    <Cell
      align="center"
      onlyVisibleOnHover
      cursor={'pointer'}
      data-testid={props['data-testid']}
      value={
        <IconCircleX
          color={GreyGrey50}
          onClick={props.onDelete}
          aria-label="Delete"
        />
      }
    />
  )
}

const ActionCell = (props: { children: ReactNode; 'data-testid'?: string }) => {
  return (
    <Cell
      onClick={e => {
        e.stopPropagation()
        return
      }}
      style={{
        padding: '8px 0 0 8px',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start'
      }}
      data-testid={props['data-testid']}
      value={props.children}
    />
  )
}

const SelectProductCell = (props: {
  phaseId: string
  priceId: string
  hasMinimum?: boolean
  'data-testid'?: string
}) => {
  const setDrawer = useSetAtom(taskDrawerConfigAtom)

  return (
    <Cell
      data-testid={props['data-testid']}
      value={
        <Flex
          gap="4px"
          alignItems="center"
          color={IndigoIndigo60}
          cursor="pointer"
          onClick={() => {
            setDrawer({
              type: 'PRICE',
              phaseId: props.phaseId,
              priceId: props.priceId
            })
          }}
        >
          {props.hasMinimum && <IconArrowCornerDownRight color={GreyGrey60} />}
          <IconPlus color="inherit" />
          <Text {...Primary13Regular} color="inherit">
            Assign product
          </Text>
        </Flex>
      }
    />
  )
}

const BillingTypeCell = ({
  billingType,
  'data-testid': testId
}: {
  billingType: Price['billingType']
  'data-testid'?: string
}) => {
  return <Cell data-testid={testId} value={toBillingType(billingType)} />
}

const BillingFrequencyCell = ({
  billingFrequency,
  'data-testid': testId
}: {
  billingFrequency: Price['billingFrequency']
  'data-testid'?: string
}) => {
  return (
    <Cell
      data-testid={testId}
      value={
        <Flex gap="4px">
          <IconArrowsRepeat color="inherit" />
          <Text {...Primary13Regular} color="inherit">
            {toBillingFrequencyLabel(billingFrequency)}
          </Text>
        </Flex>
      }
    />
  )
}

const PriceNameCell = ({
  name,
  hasMinimum,
  'data-testid': testId
}: {
  name: string
  hasMinimum?: boolean
  'data-testid'?: string
}) => {
  return (
    <Cell
      data-testid={testId}
      value={
        <Flex gap="4px">
          {hasMinimum && (
            <Box>
              <IconArrowCornerDownRight color={GreyGrey60} />
            </Box>
          )}
          <Text {...Primary13Regular} color="inherit">
            {name}
          </Text>
        </Flex>
      }
    />
  )
}

export const SchedulePriceTable = Object.assign(SchedulePriceTableComponent, {
  Row,
  Cell,
  Header,
  DiscountCell,
  MinimumCell,
  PriceCell,
  DeleteCell,
  ActionCell,
  SelectProductCell,
  PriceNameCell,
  BillingTypeCell,
  BillingFrequencyCell
})
